/**
 * The spine, as the engine defines it, and what a stored draft did with it.
 *
 * ⚠ TRANSCRIBED, NOT IMPORTED. The source of truth is
 * `backend/api/services/landing_dsh/__init__.py::SPINE` — 14 bands, fixed
 * order, each with the one-word reason the receipt records when the band does
 * not ship. The engine comment on `trust` says the string is a contract with
 * the gate side; it is a contract with this file too. If SPINE changes, this
 * changes, and the reason text a user reads changes with it. Nothing here
 * invents a reason of its own.
 */

export type BandVerdict = 'must' | 'optional';

export type SpineBand = {
  id: string;
  verdict: BandVerdict;
  /** The engine's own tag when the band does not ship. */
  skipReason: string;
};

// Every band is writer-owned now, so there is no per-band "chrome" flag: an
// absent band with no receipt is `unknown`, never inferred to be host-drawn.
// (Header/footer being pinned for REORDERING is a separate product rule in
// `draftLayout.ts`, not a property of the band here.)
export const SPINE: readonly SpineBand[] = [
  { id: 'announcement', verdict: 'optional', skipReason: 'no-offer' },
  { id: 'header', verdict: 'must', skipReason: 'missing' },
  { id: 'hero', verdict: 'must', skipReason: 'missing' },
  { id: 'trust', verdict: 'must', skipReason: 'no-trust-data' },
  { id: 'problem', verdict: 'must', skipReason: 'missing' },
  { id: 'benefits', verdict: 'must', skipReason: 'missing' },
  { id: 'services', verdict: 'must', skipReason: 'missing' },
  { id: 'process', verdict: 'must', skipReason: 'missing' },
  { id: 'before-after', verdict: 'optional', skipReason: 'no-photos' },
  { id: 'reviews', verdict: 'must', skipReason: 'no-reviews' },
  { id: 'pricing', verdict: 'optional', skipReason: 'no-price' },
  { id: 'faq', verdict: 'must', skipReason: 'missing' },
  { id: 'final-cta', verdict: 'must', skipReason: 'missing' },
  { id: 'footer', verdict: 'must', skipReason: 'missing' },
] as const;

export type SlotState = 'present' | 'absent' | 'unknown';

export type SpineSlot = {
  bandId: string;
  verdict: BandVerdict;
  state: SlotState;
  /** Why this slot cannot be swapped, in the engine's words where there are any. */
  reason: string | null;
  /** Only a band the page actually carries can be swapped slot for slot. */
  offerable: boolean;
};

/** `meta.skipped_bands` as the receipt records it: [{band, reason}]. */
type SkippedBand = { band?: unknown; reason?: unknown };

function skipReasons(copyMeta: Record<string, unknown> | null | undefined): Map<string, string> {
  const out = new Map<string, string>();
  const raw = (copyMeta?.skipped_bands ?? (copyMeta?.meta as Record<string, unknown> | undefined)?.skipped_bands) as
    | SkippedBand[]
    | undefined;
  if (!Array.isArray(raw)) return out;
  for (const entry of raw) {
    const band = typeof entry?.band === 'string' ? entry.band : null;
    const reason = typeof entry?.reason === 'string' ? entry.reason : null;
    if (band) out.set(band, reason ?? '');
  }
  return out;
}

/**
 * The spine of one stored draft: every band in engine order, what the page did
 * with it, and whether this slot can be swapped.
 *
 * A band the page carries is offerable - every band is writer-owned now, so
 * the same presence test governs announcement/header/footer too. A band that
 * did not ship is shown with the engine's own reason - the receipt carries one
 * for every absent band, so an absent band always says why rather than merely
 * being missing. When a band is absent with no receipt entry the state is
 * `unknown` and says the contract is missing; nothing is inferred about who, if
 * anyone, drew it. It is never quietly rendered as a skip.
 */
export function readSpine(args: {
  presentBandIds: readonly string[];
  copyMeta?: Record<string, unknown> | null;
}): SpineSlot[] {
  const present = new Set(args.presentBandIds.map((id) => id.trim().toLowerCase()).filter(Boolean));
  const skipped = skipReasons(args.copyMeta);

  return SPINE.map((band): SpineSlot => {
    // Every band is writer-owned now. A band the draft actually carries as a
    // section is offerable slot-for-slot, announcement/header/footer included.
    if (present.has(band.id)) {
      return { bandId: band.id, verdict: band.verdict, state: 'present', reason: null, offerable: true };
    }
    if (skipped.has(band.id)) {
      const reason = skipped.get(band.id) as string;
      return {
        bandId: band.id,
        verdict: band.verdict,
        state: 'absent',
        reason: reason || band.skipReason,
        offerable: false,
      };
    }
    // Absent, and the receipt says nothing - for ANY band, chrome or not. We do
    // not know why it is missing and will not infer a host drew it: the
    // provenance contract simply is not there.
    return {
      bandId: band.id,
      verdict: band.verdict,
      state: 'unknown',
      reason: 'its receipt does not say why it is absent',
      offerable: false,
    };
  });
}

/** Bands the page carries that the spine does not name. Shown, never hidden. */
export function strayBands(presentBandIds: readonly string[]): string[] {
  const known = new Set(SPINE.map((band) => band.id));
  return [...new Set(presentBandIds.map((id) => id.trim().toLowerCase()))]
    .filter((id) => id && !known.has(id));
}
